import Navigation from "../components/Navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Taxes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === "/") {
      // On home page, scroll to contact section
      const element = document.querySelector("#contact");
      if (element) {
        const navHeight = 80;
        const elementPosition = element.offsetTop - navHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
        window.history.pushState(null, null, "#contact");
      }
    } else {
      // On other pages, navigate to home page with contact section
      navigate("/#contact");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContactClick();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Skip to main content
      </a>

      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy1.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy1.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy1.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy1.heroResults.results.taxReduction.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.taxReduction.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy1.heroResults.results.netCash.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.netCash.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy1.heroResults.results.substance.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.substance.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy1.heroResults.results.ipDeferral.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.ipDeferral.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy1.heroResults.results.digitalProcesses.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.digitalProcesses.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy1.heroResults.results.uaeSetup.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.heroResults.results.uaeSetup.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy1.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("taxes.caseStudy1.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy1.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("taxes.caseStudy1.complication.issues.ipReserves.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.complication.issues.ipReserves.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("taxes.caseStudy1.complication.issues.residence.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.complication.issues.residence.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("taxes.caseStudy1.complication.issues.uaeCit.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.complication.issues.uaeCit.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy1.complication.issues.paymentFlows.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.complication.issues.paymentFlows.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy1.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("taxes.caseStudy1.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("taxes.caseStudy1.catalyst.table.step")}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("taxes.caseStudy1.catalyst.table.result")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.deRegistration.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.deRegistration.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.freeZoneLLC.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.freeZoneLLC.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t("taxes.caseStudy1.catalyst.steps.ipTransfer.step")}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.ipTransfer.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t("taxes.caseStudy1.catalyst.steps.substance.step")}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.substance.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.customerContracts.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy1.catalyst.steps.customerContracts.result"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy1.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("taxes.caseStudy1.impact.table.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy1.impact.table.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy1.impact.table.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy1.impact.table.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy1.impact.metrics.taxRate.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy1.impact.metrics.taxRate.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy1.impact.metrics.taxRate.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy1.impact.metrics.taxRate.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy1.impact.metrics.netCash.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy1.impact.metrics.netCash.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy1.impact.metrics.netCash.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy1.impact.metrics.netCash.delta")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy1.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.substance.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.substance.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.noWithholding.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.noWithholding.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.dividend.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.dividend.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("taxes.caseStudy1.successFactors.factors.atadCfc.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.atadCfc.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.documentation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy1.successFactors.factors.documentation.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("taxes.caseStudy1.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("taxes.caseStudy1.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("taxes.caseStudy1.cta.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("taxes.caseStudy1.cta.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105"
              >
                {t("taxes.caseStudy1.cta.button")}
              </button>
            </div>
          </div>

          {/* Second Case Study */}
          <div className="mt-20 mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy2.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy2.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy2.heroResults.results.netWorth.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.netWorth.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy2.heroResults.results.liquidityReserve.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.liquidityReserve.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy2.heroResults.results.taxReduction.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.taxReduction.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy2.heroResults.results.equityLoft.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.equityLoft.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy2.heroResults.results.retirementCapital.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.retirementCapital.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy2.heroResults.results.structured.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.heroResults.results.structured.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("taxes.caseStudy2.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy2.complication.issues.volatileCashflows.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.complication.issues.volatileCashflows.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy2.complication.issues.highTaxBurden.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.complication.issues.highTaxBurden.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("taxes.caseStudy2.complication.issues.noEquity.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.complication.issues.noEquity.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy2.complication.issues.unclearRetirement.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.complication.issues.unclearRetirement.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("taxes.caseStudy2.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.financialInventory.step"
                          )}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.financialInventory.result"
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.financialInventory.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.financialInventory.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.liquidityPlan.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.liquidityPlan.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.taxOptimization.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.taxOptimization.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.investmentPlan.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.investmentPlan.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.realEstateModel.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.realEstateModel.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t("taxes.caseStudy2.catalyst.steps.monitoring.step")}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy2.catalyst.steps.monitoring.result"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Planungs-Highlights */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.planningHighlights.title")}
              </h3>
              <div className="space-y-4">
                {t("taxes.caseStudy2.planningHighlights.highlights", {
                  returnObjects: true,
                }).map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>{highlight}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy2.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("taxes.caseStudy2.impact.table.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy2.impact.table.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy2.impact.table.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy2.impact.table.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy2.impact.metrics.netWorth.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.netWorth.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.netWorth.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy2.impact.metrics.netWorth.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "taxes.caseStudy2.impact.metrics.liquidityReserve.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy2.impact.metrics.liquidityReserve.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy2.impact.metrics.liquidityReserve.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "taxes.caseStudy2.impact.metrics.liquidityReserve.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy2.impact.metrics.taxRate.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.taxRate.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.taxRate.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy2.impact.metrics.taxRate.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy2.impact.metrics.equityLoft.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.equityLoft.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy2.impact.metrics.equityLoft.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy2.impact.metrics.equityLoft.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "taxes.caseStudy2.impact.metrics.retirementCapital.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy2.impact.metrics.retirementCapital.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy2.impact.metrics.retirementCapital.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "taxes.caseStudy2.impact.metrics.retirementCapital.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy2.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.cashflowFirst.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.cashflowFirst.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.taxLeverage.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.taxLeverage.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.disciplinedETF.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.disciplinedETF.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.quarterlyMonitoring.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.quarterlyMonitoring.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.coordinatedAdvice.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy2.successFactors.factors.coordinatedAdvice.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("taxes.caseStudy2.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("taxes.caseStudy2.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final Call to Action */}
          <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
              {t("taxes.caseStudy1.cta.title")}
            </h3>
            <p className="text-lg mb-6">
              {t("taxes.caseStudy1.cta.description")}
            </p>
            <button
              onClick={handleContactClick}
              onKeyDown={handleKeyDown}
              className="bg-[#C5A66A] text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105"
            >
              {t("taxes.caseStudy1.cta.button")}
            </button>
          </div>

          {/* Third Case Study */}
          <div className="mt-20 mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy3.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy3.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy3.heroResults.results.taxReduction.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.taxReduction.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy3.heroResults.results.retainedProfits.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.retainedProfits.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy3.heroResults.results.workingCapital.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.workingCapital.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy3.heroResults.results.cashConversion.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.cashConversion.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "taxes.caseStudy3.heroResults.results.implementation.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.implementation.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("taxes.caseStudy3.heroResults.results.eResidency.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.heroResults.results.eResidency.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("taxes.caseStudy3.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy3.complication.issues.highTaxableIncome.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.complication.issues.highTaxableIncome.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy3.complication.issues.capitalBinding.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.complication.issues.capitalBinding.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "taxes.caseStudy3.complication.issues.trademarkPrivate.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.complication.issues.trademarkPrivate.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("taxes.caseStudy3.complication.issues.ossIoss.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.complication.issues.ossIoss.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("taxes.caseStudy3.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("taxes.caseStudy3.catalyst.table.step")}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("taxes.caseStudy3.catalyst.table.result")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t("taxes.caseStudy3.catalyst.steps.eResidency.step")}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.eResidency.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.brandTransfer.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.brandTransfer.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.licenseContract.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.licenseContract.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.ossRegistration.step"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "taxes.caseStudy3.catalyst.steps.ossRegistration.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t("taxes.caseStudy3.catalyst.steps.deGmbh.step")}
                        </td>
                        <td className="px-6 py-4">
                          {t("taxes.caseStudy3.catalyst.steps.deGmbh.result")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Substance Light */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.substanceLight.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("taxes.caseStudy3.substanceLight.description")}
                </p>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("taxes.caseStudy3.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("taxes.caseStudy3.impact.table.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy3.impact.table.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy3.impact.table.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("taxes.caseStudy3.impact.table.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy3.impact.metrics.taxRate.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy3.impact.metrics.taxRate.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("taxes.caseStudy3.impact.metrics.taxRate.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("taxes.caseStudy3.impact.metrics.taxRate.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "taxes.caseStudy3.impact.metrics.retainedProfits.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.retainedProfits.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.retainedProfits.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "taxes.caseStudy3.impact.metrics.retainedProfits.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "taxes.caseStudy3.impact.metrics.workingCapital.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.workingCapital.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.workingCapital.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "taxes.caseStudy3.impact.metrics.workingCapital.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("taxes.caseStudy3.impact.metrics.amazonPayout.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.amazonPayout.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "taxes.caseStudy3.impact.metrics.amazonPayout.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "taxes.caseStudy3.impact.metrics.amazonPayout.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("taxes.caseStudy3.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("taxes.caseStudy3.successFactors.factors.zeroCIT.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.zeroCIT.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.trademarkProtection.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.trademarkProtection.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.ossCentral.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.ossCentral.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.dividendExemption.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.dividendExemption.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("taxes.caseStudy3.successFactors.factors.noCFC.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "taxes.caseStudy3.successFactors.factors.noCFC.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("taxes.caseStudy3.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("taxes.caseStudy3.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final Call to Action */}
          <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
              {t("taxes.finalCta.title")}
            </h3>
            <p className="text-lg mb-6">{t("taxes.finalCta.description")}</p>
            <button
              onClick={handleContactClick}
              onKeyDown={handleKeyDown}
              className="bg-[#C5A66A] text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105"
            >
              {t("taxes.finalCta.button")}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Taxes;
